import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import type { Currency, InvoiceStatus, MasterRef } from '@/lib/types';
import type { ComboboxOption } from '@/components/ui';

/**
 * The invoice status list.
 *
 * Fetched WITHOUT the `isActive` filter on purpose. Filtering server-side would
 * give a clean create dropdown but would drop the status an invoice already
 * holds once that status was retired — which reads as "no status" and would
 * clear the field on the next save. Everything is fetched once and the
 * create/edit split is made at render time by `selectableStatuses` below.
 *
 * `perPage: 200` is this endpoint's ceiling, and its default of 50 is not the
 * whole table. Note the ceiling differs from the deal pickers' 100 — the two
 * families look alike and are not.
 *
 * Unlike `/invoices`, this route requires a bearer token *today*, and an
 * expired one means the dropdown is the only part of the screen that fails.
 * `failed` is surfaced rather than swallowed so the form can say so.
 */
export function useInvoiceStatusOptions(enabled = true) {
  const [statuses, setStatuses] = useState<InvoiceStatus[]>([]);
  // Starts true when enabled so the first paint shows a loading picker rather
  // than an empty one, and nothing calls setState synchronously in an effect.
  const [loading, setLoading] = useState(enabled);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    api
      .listInvoiceStatuses({ page: 1, perPage: 200 }, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        setStatuses(result.data);
        setFailed(false);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setStatuses([]);
        setFailed(true);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [enabled]);

  return { statuses, loading, failed };
}

/**
 * The statuses a dropdown may offer, given what the invoice currently holds.
 *
 * A retired status is hidden from new selections but kept — and marked — when
 * it is the record's own value. The server checks that a `statusId` exists but
 * deliberately does NOT check `isActive`, so a retired status is still savable;
 * the only thing that must not happen is silently losing it.
 *
 * The list arrives in curated `sortOrder ASC, name ASC` order. Do not re-sort.
 */
export function selectableStatuses(
  statuses: InvoiceStatus[],
  current: MasterRef | null,
): ComboboxOption[] {
  const options = statuses
    .filter((status) => status.isActive || status.id === current?.id)
    .map((status) => ({
      value: String(status.id),
      label: status.isActive ? status.name : `${status.name} (retired)`,
    }));

  // An empty list means "still loading", not "every option was retired", so the
  // stored value is only pinned back once there is a list to be missing from.
  if (current && options.length > 0 && !options.some((o) => o.value === String(current.id))) {
    options.unshift({ value: String(current.id), label: `${current.name} (no longer offered)` });
  }
  return options;
}

/**
 * The currency list — 164 rows, mirrored from the SEED directory.
 *
 * `perPage: 250` is this endpoint's own ceiling and clears the whole table in
 * one request, so there is no paging to handle. `isActive: true` because a
 * deactivated currency should not be offered for a new invoice.
 *
 * The rows arrive ordered by `currencyName` ascending — there is no `sortOrder`
 * on this master, the one place it diverges from the others.
 */
export function useCurrencyOptions(enabled = true) {
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    api
      .listCurrencies({ page: 1, perPage: 250, isActive: true }, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        setCurrencies(result.data);
        setFailed(false);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setCurrencies([]);
        setFailed(true);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [enabled]);

  return { currencies, loading, failed };
}

/**
 * Currency options for a Combobox.
 *
 * **The value is the three-letter code, never the id.** An invoice stores
 * `currency` as a `CHAR(3)` with no foreign key to the currency table, so an id
 * here would be saved as a number and rejected — or worse, coerced.
 *
 * A code already on the invoice is pinned back in if the list does not carry
 * it, which covers both a deactivated currency and one the directory dropped.
 */
export function currencyOptions(currencies: Currency[], current: string | null): ComboboxOption[] {
  const options = currencies.map((currency) => ({
    value: currency.currencyCode,
    label: currency.currencyCode,
    hint: currency.currencyName,
  }));

  if (current && options.length > 0 && !options.some((o) => o.value === current)) {
    options.unshift({ value: current, label: current, hint: 'No longer offered' });
  }
  return options;
}
